import { z } from 'zod';
import { TextBloc } from '@/components/TextBlock';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetData } from '@/hooks/useWidgetData';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { fontOptionsSchema } from '@/lib/fontStyle';
import { substituteVariables, textReplacementSchema, type TextReplacement } from '@/lib/text';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A single line of `{{variable}}` text driven by an MQTT topic. Port of
 * `textWidget/TextWidget.vue`.
 *
 * Used by four deployed configs, all of them for the same sentence:
 * `"There are {{meetingRoomAvailable}} spaces ready for a meeting."`
 */

const optionsSchema = z.object({
  topic: z.string().optional(),
  layout: widgetLayoutSchema.optional(),
  textBloc: z
    .object({
      text: z.string().default(''),
      /**
       * Post-substitution fix-ups. **Both spellings are read**, because the legacy code and
       * the deployed configs disagree: `TextWidget.vue` reads `replace`, and all four
       * configs that use this widget spell it `replaced` (docs/MIGRATION-WIDGETS.md §3).
       *
       * The consequence is a visible change — see the note on `resolveText` below.
       */
      replace: z.array(textReplacementSchema).optional(),
      /** [sic] The spelling every deployed config actually uses. */
      replaced: z.array(textReplacementSchema).optional(),
    })
    .prefault({}),
  textBlocFont: fontOptionsSchema.optional(),
});

type TextBlocOptions = z.infer<typeof optionsSchema>['textBloc'];

/**
 * A flat map of substitution values (docs/MIGRATION-WIDGETS.md §7). Values are deliberately
 * `unknown`: `substituteVariables` accepts the primitives and treats everything else as a
 * missing key, so one unexpected nested object in a payload costs one variable rather than
 * the whole message.
 */
const payloadSchema = z.record(z.string(), z.unknown());

export function TextWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const data = useWidgetData<unknown>(componentId, opts?.topic);
  const { className, style } = useWidgetLayout(opts?.layout);

  if (!parsed.success) {
    warnOnce(
      `text-widget-options:${componentId}`,
      '[signage] TextWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  // `parsed.data` rather than `opts`: TypeScript does not relate the narrowing of `parsed` to
  // the separate const the hooks above had to read from.
  const { textBloc, textBlocFont } = parsed.data;

  return (
    <div className={className} style={style}>
      <div className="w-full text-center">
        <TextBloc text={resolveText(textBloc, data, componentId)} font={textBlocFont} />
      </div>
    </div>
  );
}

/**
 * The template with its variables substituted, or `''` before the first message.
 *
 * Two behaviours worth being explicit about:
 *
 * **The empty state is `''`,** i.e. the widget renders nothing at all until a message
 * arrives (docs/MIGRATION-WIDGETS.md §9). The legacy guard was `if (options.textBloc.text &&
 * data.value)`, and Vuex initialised `data` to `{}` — which is truthy — so the legacy app
 * actually rendered `"There are N/A spaces ready for a meeting."` on a dead feed. The store
 * here returns `undefined` until the first message, which makes the guard behave the way §9
 * documents. Deliberate: `N/A` on a wall reads as a broken widget, an empty gradient box
 * reads as a feed that has not arrived.
 *
 * **`replaced` now takes effect.** No deployed config carries `replace`, so the legacy
 * `is 1 space` fix-up has never run and four displays render "There are 1 spaces ready for a
 * meeting." whenever exactly one room is free. Reading both keys (§3) makes the config do
 * what it plainly intends. Recorded as W-15 for SYM-506 to sign off.
 */
function resolveText(
  textBloc: TextBlocOptions,
  data: unknown,
  componentId: string,
): string {
  if (!textBloc.text || data === undefined) return '';

  const payload = payloadSchema.safeParse(data);
  if (!payload.success) {
    warnOnce(
      `text-widget-payload:${componentId}`,
      '[signage] TextWidget payload is not an object of values; rendering no text. %s',
      z.prettifyError(payload.error),
    );
    return '';
  }

  const replace: readonly TextReplacement[] | undefined = textBloc.replace ?? textBloc.replaced;
  return substituteVariables(textBloc.text, payload.data, { missing: 'na', replace });
}
